/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 */
import {HttpClient, HttpErrorResponse, HttpHeaders, HttpParams, HttpResponse} from '@angular/common/http';
import {forwardRef, Inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';

import {TranslateService} from '@ngx-translate/core';

import {EMPTY, Observable, of} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';

import {REQUEST_OPTIONS, ResponseStatusCodes} from '../../app.constants';
import {AuthenticationService} from '../authentication/authentication.service';
import {FileDescription} from '../files/files.model';
import {ModalService} from '../modal/modal.service';
import {isPresent} from '../utils/lang';


/**
 * Options for API requests
 */
export interface ApiRequestOptions {
  /**
   * The request options for the underlying HTTP call.
   */
  requestOptions?: any;
}

const DEFAULT_API_OPTIONS: ApiRequestOptions = {};

/**
 * Endpoint (POST) used to start a process instance.
 */
const START_PROCESS_INSTANCE_PATH = '/workflowable/requests';

/**
 * Error message keys returned by the BE on the startProcessInstance endpoint that should be
 * displayed (translated) to the user instead of the generic error modal.
 */
const START_PROCESS_INSTANCE_ERROR_KEYS = [
  'ERROR.no_validator_for_request',
  'ERROR.no_email_for_request'
];


interface RequestOptions {
  headers?: HttpHeaders | {
    [header: string]: string | string[];
  };
  observe?: 'body';
  params?: HttpParams | {
    [param: string]: string | string[];
  };
  reportProgress?: boolean;
  responseType?: 'json';
  withCredentials?: boolean;
}

/**
 * API service that wraps HTTP calls injecting the Bearer token and hits the proper host / path prefix.
 */
@Injectable()
export class ApiService {

  // aliases for microservices
  public authent = new PrefixedApiService('/authent/api/v1', this);
  public communication = new PrefixedApiService('/communication/api/v1', this);
  public config = new PrefixedApiService('/repository/api/v1', this);
  public ga = new PrefixedApiService('/ga/api/v1', this);
  public elasticquery = new PrefixedApiService('/elasticquery/api/v1', this);
  public ga20 = new PrefixedApiService('/ga/api/v2', this);
  public payroll = new PrefixedApiService('/payroll/api/v1', this);
  public payment = new PrefixedApiService('/payment/api/v1', this);
  public report = new PrefixedApiService('/report/api/v1', this);
  public file = new PrefixedApiService('/file/api/v1', this);
  public etemplate = new PrefixedApiService('/etemplate/api/v1', this);
  public sdconnect = new PrefixedApiService('/sdconnect/api/v1', this);
  public configtool = new PrefixedApiService('/configtool-api/api/v1', this);
  public variable = new PrefixedApiService('/variable/api/v2', this);
  public ai = new PrefixedApiService('/ai', this);

  private silentApiService: SilentApiService;
  private cliCore: string;

  constructor(protected authenticationService: AuthenticationService,
              protected httpClient: HttpClient,
              protected router: Router,
              @Inject(forwardRef(() => TranslateService)) protected translate: TranslateService,
              protected modalService: ModalService,
              @Inject('GP_API_HOST') public apiHost: string) {
    const hostname = window.location.hostname;
    this.cliCore = hostname.slice(0, hostname.indexOf('.'));
  }

  get silent(): SilentApiService {
    if (!this.silentApiService) {
      this.silentApiService = new SilentApiService(
        this.authenticationService,
        this.httpClient,
        this.router,
        this.translate,
        this.modalService,
        this.apiHost
      );
    }
    return this.silentApiService;
  }

  public get(path: string,
             options?: ApiRequestOptions): Observable<any> {

    return this.getRequestOptions(options)
      .pipe(
        switchMap((opts: RequestOptions) => this.httpClient.get(`${this.apiHost}${path}`, opts)),
        tap(this.getOnSuccessHandler(), this.getOnErrorHandler())
      );
  }

  public getAndDisplayInWindow(path: string, type: string): Observable<any> {
    const openWindow = (payload: any) => {
      const file = new Blob([payload], {type});
      const objectUrl = URL.createObjectURL(file);
      window.open(objectUrl);
    };

    return this.get(path, REQUEST_OPTIONS.RESPONSE_TYPE.TEXT)
      .pipe(tap({
          next: (body) => openWindow(body),
          error: (error: HttpErrorResponse) => openWindow(error.error)
        })
      );
  }

  public post(path: string,
              body?: any,
              options?: ApiRequestOptions): Observable<any> {

    return this.getRequestOptions(options)
      .pipe(
        switchMap((opts: RequestOptions) => this.httpClient.post(`${this.apiHost}${path}`, body, opts)),
        tap(this.getOnSuccessHandler(), this.getOnErrorHandler())
      );
  }

  public put(path: string,
             body: any,
             options?: ApiRequestOptions): Observable<any> {

    return this.getRequestOptions(options)
      .pipe(
        switchMap((opts: RequestOptions) => this.httpClient.put(`${this.apiHost}${path}`, body, opts)),
        tap(this.getOnSuccessHandler(), this.getOnErrorHandler())
      );
  }

  public delete(path: string,
                options?: ApiRequestOptions): Observable<any> {

    return this.getRequestOptions(options)
      .pipe(
        switchMap((opts: RequestOptions) => this.httpClient.delete(`${this.apiHost}${path}`, opts)),
        tap(this.getOnSuccessHandler(), this.getOnErrorHandler())
      );
  }

  public patch(path: string,
               body: string,
               options?: ApiRequestOptions): Observable<any> {

    return this.getRequestOptions(options)
      .pipe(
        switchMap((opts: RequestOptions) => this.httpClient.patch(`${this.apiHost}${path}`, body, opts)),
        tap(this.getOnSuccessHandler(), this.getOnErrorHandler())
      );
  }

  public head(path: string,
              options?: ApiRequestOptions): Observable<any> {

    return this.getRequestOptions(options)
      .pipe(
        switchMap((opts: RequestOptions) => this.httpClient.head(`${this.apiHost}${path}`, opts)),
        tap(this.getOnSuccessHandler(), this.getOnErrorHandler())
      );
  }

  public uploadFile(path: string,
                    file: Blob,
                    options?: any): Observable<any> {
    const formData: FormData = new FormData();
    const uploadOptions = Object.assign({}, {
      fileParamName: 'file',
      additionalData: {},
      headers: {}
    }, options);

    formData.append(
      uploadOptions.fileParamName,
      file,
      isPresent(uploadOptions.fileName) ? uploadOptions.fileName : 'file'
    );

    Object.keys(uploadOptions.additionalData)
      .forEach(key => {
        formData.append(key, uploadOptions.additionalData[key]);
      });

    return this.getRequestOptions(options)
      .pipe(
        switchMap((opts: RequestOptions) => this.httpClient.post(`${this.apiHost}${path}`, formData, opts)),
        tap(this.getOnSuccessHandler(), this.getOnErrorHandler())
      );
  }

  public deleteFile(path: string): Observable<any> {
    return this.httpClient.delete(`${this.apiHost}${path}`);
  }

  /**
   * Get actual HTTP request options to use based on passed API request options. Sets some additional
   * values (such as Authorization and Content-Type headers).
   *
   * @param options {ApiRequestOptions} the api request options to get HTTP options from
   */
  protected getRequestOptions(options: ApiRequestOptions): Observable<RequestOptions> {
    const needsToken = !options || !options.requestOptions || options.requestOptions.withCredentials !== false;
    const disableCache = !options || !options.requestOptions || options.requestOptions.disableCache !== false;
    return (needsToken ? this.authenticationService.getToken() : of(null))
      .pipe(
        switchMap((token: string) => {
          if (needsToken && !token) {
            return EMPTY;
          }

          const apiOptions: ApiRequestOptions = isPresent(options)
            ? Object.assign({}, options)
            : DEFAULT_API_OPTIONS;

          const actualOptions: any = Object.assign({
            headers: new HttpHeaders(),
            disableCache: disableCache
          }, apiOptions.requestOptions || {});

          if (needsToken && actualOptions.headers.get('Authorization') === null && token) {
            actualOptions.headers = actualOptions.headers
              .append('Authorization', `Bearer ${token}`);
          }
          actualOptions.headers = actualOptions.headers.append('User-Locale', this.translate.currentLang);

          // todo these headers should be removed. Cache control should be implemented on BE side
          if (actualOptions.disableCache) {
            // If "Cache-Control" isn't set, all browsers work corectly except IE. IE decides that request can be cached
            actualOptions.headers = actualOptions.headers.append('Pragma', 'no-cache');
            actualOptions.headers = actualOptions.headers.append('Cache-Control', 'no-cache');
            // actualOptions.headers.append('If-Modified-Since', 'Mon, 26 Jul 1997 05:00:00 GMT')
          }

          return of(actualOptions);
        })
      );
  }

  /**
   * Builds an interceptor function for successful API calls.
   * @param options the API call options
   */
  protected getOnSuccessHandler(): (r: any) => void {
    return (response: HttpResponse<any>) => {

      // if (!response) {
      //   return;
      // }

      /* Only here for the mock backend that returns success for all response codes.
       TODO: use mockBackend#mockError when it can accept a response instead/in addition to an Error.
       See https://github.com/angular/angular/blob/master/modules/@angular/http/testing/mock_backend.ts#L91 */
      if (response && response.status === 401) {
        this.authenticationService.clearUser();
      }
    };
  }

  /**
   * Builds an interceptor function for API calls on error.
   */
  protected getOnErrorHandler(): (e: any) => void {
    return (response: HttpErrorResponse) => {
      if (!response) {
        return;
      }

      const technicalDetailsForCopy = `correlationId: ${response.headers.get('x-correlation-id')}, status: ${response.status}, url: ${response.url}`;

      // Specific handling for the startProcessInstance endpoint (POST /workflowable/requests):
      // when the BE returns a "no validator" / "no email" error, show the translated message instead of the generic modal.
      const startProcessInstanceErrorOptions = this.getStartProcessInstanceErrorModalOptions(response, technicalDetailsForCopy);
      if (startProcessInstanceErrorOptions) {
        this.modalService.apiError(startProcessInstanceErrorOptions);
        return;
      }

      switch (response.status) {
        case ResponseStatusCodes.UnauthorizedCode:
          break;
        case ResponseStatusCodes.ConflictCode:
          this.modalService.apiError({
            type: 'WARNING',
            title: 'ERROR._409.title',
            titleIcon: 'fa-lock',
            description: response.error?.message || 'ERROR._409.description',
            technicalDetailsForCopy: technicalDetailsForCopy
          });
          break;
        case ResponseStatusCodes.ForbiddenCode:
          this.modalService.apiError({
            type: 'WARNING',
            title: 'ERROR._403.title',
            titleIcon: 'fa-ban',
            description: 'ERROR._403.description',
            technicalDetailsForCopy: technicalDetailsForCopy
          });
          break;
        case ResponseStatusCodes.Timeout:
          this.modalService.apiError({
            type: 'WARNING',
            titleIcon: 'fa-plug-circle-exclamation',
            title: 'ERROR._524.title',
            description: 'ERROR._524.description',
            technicalDetailsForCopy: technicalDetailsForCopy
          });
          break;
        case ResponseStatusCodes.NetworkAuthenticationRequiredCode:
          return response;
        default:
          this.modalService.apiError(this.getApiErrorModalOptionsIfBlockingError(response) || {
            type: 'DANGER',
            title: 'ERROR.other.title',
            titleIcon: 'fa-circle-exclamation',
            description: 'ERROR._common.description',
            technicalDetailsForCopy: technicalDetailsForCopy,
            withIllustration: true
          });
          break;
      }
    };
  }

  /**
   * Returns modal options to display a translated error message when the failing request is the
   * startProcessInstance call (POST /workflowable/requests) and the BE returned a "no validator"
   * or "no email" error. Returns null when the case does not apply.
   */
  private getStartProcessInstanceErrorModalOptions(response: HttpErrorResponse, technicalDetailsForCopy: string): {
    type?: 'DANGER' | 'WARNING';
    title?: string;
    titleIcon?: string;
    description?: string,
    technicalDetailsForCopy?: string
  } {
    const url = response.url || '';
    // POST /workflowable/requests (ignore sub-paths such as /cancel by removing query params first)
    const isStartProcessInstance = url.split('?')[0].endsWith(START_PROCESS_INSTANCE_PATH);
    if (!isStartProcessInstance) {
      return null;
    }

    const message = response.error?.message;
    if (!START_PROCESS_INSTANCE_ERROR_KEYS.includes(message)) {
      return null;
    }

    return {
      type: 'WARNING',
      title: 'ERROR.other.title',
      titleIcon: 'fa-triangle-exclamation',
      description: message,
      technicalDetailsForCopy: technicalDetailsForCopy
    };
  }

  private getApiErrorModalOptionsIfBlockingError(response: HttpErrorResponse): {
    type?: 'DANGER' | 'WARNING';
    title?: string;
    titleIcon?: string;
    description?: string,
    details?: string[],
    technicalDetailsForCopy?: string
  } {
    const errorBody = response.error;
    // check directly on errorBody and in errorBody.details because it can come from ga or workflowable
    if (errorBody?.code !== 1002 && errorBody?.details?.code !== 1002) {
      return null;
    }
    return {
      type: 'WARNING',
      title: 'FORM_GENERATOR.BLOCKING_ERRORS.title',
      titleIcon: 'fa-triangle-exclamation',
      description: 'FORM_GENERATOR.BLOCKING_ERRORS.text',
      details: (errorBody?.details?.details ?? errorBody?.details ?? []).map(alert => alert.transId)
    };
  }
}

/**
 * This class is a delegate of ApiService which allows to add a prefix to the url api.
 * Example of usage :
 *
 * @Injectable()
 export class ApiService {
 public config = new PrefixedApiService('/config/api/v1', this);
 }

 @Injectable()
  export class EmployeesService {
  constructor(private api: ApiService) {}

  public getMenuItems(cliRegId: number): Observable<any> {
  this.api.config.get(`/regulation/report?cliRegs=${cliRegId}&type=CERTIFICATE`);
  //this is equivalent to :
  //this.api.get(`/config/api/v1/regulation/report?cliRegs=${cliRegId}&type=CERTIFICATE`);
  }
  }
 */
export class PrefixedApiService {

  public silentPrefixedApiService: PrefixedApiService;

  constructor(
    private apiPath: string,
    private apiService: ApiService) {
  }

  get prefix(): string {
    return this.apiPath;
  }

  get silent(): PrefixedApiService {
    if (!this.silentPrefixedApiService) {
      this.silentPrefixedApiService = new PrefixedApiService(this.apiPath, this.apiService.silent);
    }
    return this.silentPrefixedApiService;
  }

  public get(path: string, options?: ApiRequestOptions): Observable<any> {
    return this.apiService.get(`${this.apiPath}${path}`, options);
  }

  public getAndDisplayInWindow(path: string, type: string): Observable<any> {
    return this.apiService.getAndDisplayInWindow(`${this.apiPath}${path}`, type);
  }

  public post(path: string, body: any, options?: ApiRequestOptions): Observable<any> {
    return this.apiService.post(`${this.apiPath}${path}`, body, options);
  }

  public put(path: string, body: any, options?: ApiRequestOptions): Observable<any> {
    return this.apiService.put(`${this.apiPath}${path}`, body, options);
  }

  public delete(path: string, options?: ApiRequestOptions): Observable<any> {
    return this.apiService.delete(`${this.apiPath}${path}`, options);
  }

  public patch(path: string, body: any, options?: ApiRequestOptions): Observable<any> {
    return this.apiService.patch(`${this.apiPath}${path}`, body, options);
  }

  public head(path: string, options?: ApiRequestOptions): Observable<any> {
    return this.apiService.head(`${this.apiPath}${path}`, options);
  }

  public uploadFile(path: string, file: Blob, options?: any): Observable<FileDescription> {
    return this.apiService.uploadFile(`${this.apiPath}${path}`, file, options);
  }

  public deleteFile(path: string): Observable<Response> {
    return this.apiService.delete(`${this.apiPath}${path}`);
  }
}

/**
 * This class overrides getOnErrorHandler to avoid showing an error modal when BE fails
 */
export class SilentApiService extends ApiService {

  protected getOnErrorHandler(): (e: any) => void {
    return;
  }
}
